import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';

import { ENDPOINTS } from '@/configs/endpoints';
import { notificationsStore } from '@/stores/global/RootStore';
import { ILocalStore } from '@/stores/interfaces/ILocalStore';
import { MetaStore } from '@/stores/local/shared/MetaStore';
import type {
  PdfDocumentNote,
  PdfNoteHighlightColor,
  PdfNoteSelection,
  PdfNotesApi,
} from '@/stores/local/shared/PdfNotesStore';
import { FieldTextStore } from '@/stores/local/shared/form/FieldTextStore';
import api from '@/utils/api';
import { schemeParse } from '@/utils/schemeParse';

import {
  MeetingNotesSseClient,
  meetingNoteSummarySchema,
  type MeetingNoteSummaryResponseApi,
} from './MeetingNotesSseClient';

type NotesStoreParams = {
  organizationId: string;
  getDocumentId: () => string | undefined;
  getCurrentPage: () => number;
  getDocumentName: () => string | undefined;
  api: PdfNotesApi;
};

type HighlightEntry = {
  noteId: string;
  rects: PdfDocumentNote['rects'];
  isActive: boolean;
  color?: PdfNoteHighlightColor;
};

type MarkerEntry = {
  noteId: string;
  point: NonNullable<PdfDocumentNote['point']>;
  isActive: boolean;
  color?: PdfNoteHighlightColor;
};

type AreaEntry = {
  noteId: string;
  path: NonNullable<PdfDocumentNote['path']>;
  isActive: boolean;
  color?: PdfNoteHighlightColor;
};

type PageNoteEntry = {
  noteId: string;
  isActive: boolean;
  color?: PdfNoteHighlightColor;
};

const mergeRectsToSingleBlock = (rects: PdfDocumentNote['rects']): PdfDocumentNote['rects'] => {
  if (rects.length === 0) {
    return [];
  }

  const minX = Math.min(...rects.map((item) => item.x));
  const minY = Math.min(...rects.map((item) => item.y));
  const maxX = Math.max(...rects.map((item) => item.x + item.width));
  const maxY = Math.max(...rects.map((item) => item.y + item.height));

  return [
    {
      x: minX,
      y: minY,
      width: Math.max(1, maxX - minX),
      height: Math.max(1, maxY - minY),
    },
  ];
};

type PrivateFields =
  | '_collection'
  | '_drawerOpened'
  | '_detailNoteId'
  | '_activeNoteId'
  | '_editingNoteId'
  | '_isCreateMode'
  | '_pendingSelection'
  | '_draftColor'
  | '_setCollection'
  | '_setDrawerOpened'
  | '_setDetailNoteId'
  | '_setActiveNoteId'
  | '_setEditingNoteId'
  | '_setCreateMode'
  | '_setPendingSelection'
  | '_setDraftColor'
  | '_aiSummaryNote'
  | '_aiSummaryStreamPreview'
  | '_setAiSummaryNote'
  | '_setAiSummaryStreamPreview';
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const INVALID_DOCUMENT_ID_MESSAGE = 'Некорректный формат document_id';

export class OrganizationDocumentPreviewPageNotesStore implements ILocalStore {
  private readonly _organizationId: string;
  private readonly _api: PdfNotesApi;
  private readonly _getDocumentId: () => string | undefined;
  private readonly _getCurrentPage: () => number;
  private readonly _getDocumentName: () => string | undefined;

  private _collection: PdfDocumentNote[] = [];
  private _drawerOpened = false;
  private _detailNoteId: string | null = null;
  private _activeNoteId: string | null = null;
  private _editingNoteId: string | null = null;
  private _isCreateMode = false;
  private _pendingSelection: PdfNoteSelection | null = null;
  private _draftColor: PdfNoteHighlightColor = 'blue';

  private _aiSummaryNote: PdfDocumentNote | null = null;
  private _aiSummaryStreamPreview = '';

  readonly meta = new MetaStore();
  readonly aiSummaryMeta = new MetaStore();
  readonly createMeta = new MetaStore();
  readonly countsMeta = new MetaStore();
  readonly counts = observable.map<string, number>();
  readonly draftCaptionField = new FieldTextStore({ defaultValue: '', sanitize: false });
  readonly draftTextField = new FieldTextStore({ defaultValue: '', sanitize: false });

  private readonly _sse = new MeetingNotesSseClient();

  private _documentReactionDisposer: (() => void) | null = null;
  private _unsubscribeRepository: (() => void) | null = null;
  private _meetingNotesAbortController: AbortController | null = null;

  constructor({ organizationId, getDocumentId, getCurrentPage, getDocumentName, api }: NotesStoreParams) {
    this._organizationId = organizationId;
    this._api = api;
    this._getDocumentId = getDocumentId;
    this._getCurrentPage = getCurrentPage;
    this._getDocumentName = getDocumentName;

    makeObservable<OrganizationDocumentPreviewPageNotesStore, PrivateFields>(this, {
      _collection: observable.ref,
      _drawerOpened: observable,
      _detailNoteId: observable,
      _activeNoteId: observable,
      _editingNoteId: observable,
      _isCreateMode: observable,
      _pendingSelection: observable.ref,
      _draftColor: observable,
      _aiSummaryNote: observable.ref,
      _aiSummaryStreamPreview: observable,
      collection: computed,
      documentId: computed,
      organizationId: computed,
      aiSummaryNote: computed,
      aiSummaryStreamPreview: computed,
      canGenerateAiDocumentSummary: computed,
      drawerOpened: computed,
      detailNoteId: computed,
      activeNoteId: computed,
      activeNote: computed,
      editingNoteId: computed,
      editingNote: computed,
      isEditMode: computed,
      isCreateMode: computed,
      hasEditChanges: computed,
      canSaveNote: computed,
      pendingSelection: computed,
      draftColor: computed,
      highlightsByPage: computed,
      markersByPage: computed,
      areasByPage: computed,
      pageNotesByPage: computed,
      _setCollection: action.bound,
      _setDrawerOpened: action.bound,
      _setDetailNoteId: action.bound,
      _setActiveNoteId: action.bound,
      _setEditingNoteId: action.bound,
      _setCreateMode: action.bound,
      _setPendingSelection: action.bound,
      _setDraftColor: action.bound,
      _setAiSummaryNote: action.bound,
      _setAiSummaryStreamPreview: action.bound,
      loadCurrentDocument: action.bound,
      loadCounts: action.bound,
      openDrawer: action.bound,
      closeDrawer: action.bound,
      openCreateFromSelection: action.bound,
      openCreateFromCurrentPage: action.bound,
      openEdit: action.bound,
      cancelCreate: action.bound,
      openDetail: action.bound,
      backToList: action.bound,
      openFromHighlight: action.bound,
      clearPendingSelection: action.bound,
      setPendingSelection: action.bound,
      setActiveNote: action.bound,
      setDraftColor: action.bound,
      deleteNote: action.bound,
      createNote: action.bound,
      resetForDocumentChange: action.bound,
      generateAiDocumentSummary: action.bound,
    });

    this._documentReactionDisposer = reaction(
      () => this.documentId,
      () => {
        this.loadCurrentDocument();
      },
      { fireImmediately: true },
    );

    this._unsubscribeRepository = this._api.subscribe((change) => {
      if (change.organizationId !== this._organizationId) {
        return;
      }

      if (!this.documentId) {
        return;
      }

      if (!change.documentId || change.documentId === this.documentId) {
        void this.loadCurrentDocument();
      }
    });
  }

  get collection() {
    return this._collection;
  }

  get organizationId() {
    return this._organizationId;
  }

  get documentId() {
    return this._getDocumentId() ?? null;
  }

  get aiSummaryNote() {
    return this._aiSummaryNote;
  }

  get aiSummaryStreamPreview() {
    return this._aiSummaryStreamPreview;
  }

  get canGenerateAiDocumentSummary() {
    return this._collection.length > 0 && Boolean(this.documentId) && !this.aiSummaryMeta.isLoading;
  }

  get drawerOpened() {
    return this._drawerOpened;
  }

  get detailNoteId() {
    return this._detailNoteId;
  }

  get activeNoteId() {
    return this._activeNoteId;
  }

  get activeNote() {
    if (!this._activeNoteId) {
      return null;
    }

    if (this._aiSummaryNote?.id === this._activeNoteId) {
      return this._aiSummaryNote;
    }

    return this._collection.find((item) => item.id === this._activeNoteId) ?? null;
  }

  get editingNoteId() {
    return this._editingNoteId;
  }

  get editingNote() {
    if (!this._editingNoteId) {
      return null;
    }

    return this._collection.find((item) => item.id === this._editingNoteId) ?? null;
  }

  get isEditMode() {
    return Boolean(this._editingNoteId);
  }

  get isCreateMode() {
    return this._isCreateMode;
  }

  get hasEditChanges() {
    const note = this.editingNote;

    if (!note) {
      return false;
    }

    const captionChanged = (this.draftCaptionField.value ?? '').trim() !== note.caption.trim();
    const textChanged = (this.draftTextField.value ?? '').trim() !== note.text.trim();
    const noteColor = note.color ?? 'blue';
    const colorChanged = this._draftColor !== noteColor;

    return captionChanged || textChanged || colorChanged;
  }

  get canSaveNote() {
    const anchorType = this.isEditMode
      ? (this.editingNote?.anchorType ?? 'text')
      : (this._pendingSelection?.anchorType ?? 'text');
    const isPointOrArea = anchorType === 'point' || anchorType === 'area';
    const hasCaptionValue = (this.draftCaptionField.value ?? '').trim().length > 0;
    const hasTextValue = isPointOrArea || (this.draftTextField.value ?? '').trim().length > 0;
    const hasChangesInEditMode = this.isEditMode ? this.hasEditChanges : true;

    return hasCaptionValue && hasTextValue && hasChangesInEditMode && !this.createMeta.isLoading;
  }

  get pendingSelection() {
    return this._pendingSelection;
  }

  get draftColor() {
    return this._draftColor;
  }

  get highlightsByPage() {
    const result = new Map<number, HighlightEntry[]>();

    this._collection.forEach((note) => {
      if (!note.rects.length) {
        return;
      }

      const current = result.get(note.pageNumber) ?? [];

      current.push({
        noteId: note.id,
        rects: note.rects,
        isActive: note.id === this._activeNoteId,
        color: note.color,
      });

      result.set(note.pageNumber, current);
    });

    return result;
  }

  get markersByPage() {
    const result = new Map<number, MarkerEntry[]>();

    this._collection.forEach((note) => {
      if (!note.point) {
        return;
      }

      const current = result.get(note.pageNumber) ?? [];

      current.push({
        noteId: note.id,
        point: note.point,
        isActive: note.id === this._activeNoteId,
        color: note.color,
      });

      result.set(note.pageNumber, current);
    });

    return result;
  }

  get areasByPage() {
    const result = new Map<number, AreaEntry[]>();

    this._collection.forEach((note) => {
      if (!note.path || note.path.length < 2) {
        return;
      }

      const current = result.get(note.pageNumber) ?? [];

      current.push({
        noteId: note.id,
        path: note.path,
        isActive: note.id === this._activeNoteId,
        color: note.color,
      });

      result.set(note.pageNumber, current);
    });

    return result;
  }

  get pageNotesByPage() {
    const result = new Map<number, PageNoteEntry[]>();

    this._collection.forEach((note) => {
      const hasRects = note.rects.length > 0;
      const hasPoint = Boolean(note.point);
      const hasPath = Boolean(note.path && note.path.length >= 2);

      if (hasRects || hasPoint || hasPath) {
        return;
      }

      const current = result.get(note.pageNumber) ?? [];

      current.push({
        noteId: note.id,
        isActive: note.id === this._activeNoteId,
        color: note.color,
      });

      result.set(note.pageNumber, current);
    });

    return result;
  }

  private _setCollection(collection: PdfDocumentNote[]) {
    this._collection = collection;
  }

  resetForDocumentChange() {
    this._cancelMeetingNotesStream();
    this._setCollection([]);
    this.meta.reset();
    this._setAiSummaryNote(null);
    this._setAiSummaryStreamPreview('');
    this.aiSummaryMeta.reset();
    this._setDetailNoteId(null);
    this._setActiveNoteId(null);
    this._setEditingNoteId(null);
    this._setCreateMode(false);
    this._setPendingSelection(null);
  }

  private _setAiSummaryNote(value: PdfDocumentNote | null) {
    this._aiSummaryNote = value;
  }

  private _setAiSummaryStreamPreview(value: string) {
    this._aiSummaryStreamPreview = value;
  }

  private _setDrawerOpened(value: boolean) {
    this._drawerOpened = value;
  }

  private _setDetailNoteId(value: string | null) {
    this._detailNoteId = value;
  }

  private _setActiveNoteId(value: string | null) {
    this._activeNoteId = value;
  }

  private _setEditingNoteId(value: string | null) {
    this._editingNoteId = value;
  }

  private _setCreateMode(value: boolean) {
    this._isCreateMode = value;
  }

  private _setPendingSelection(value: PdfNoteSelection | null) {
    this._pendingSelection = value;
  }

  private _setDraftColor(value: PdfNoteHighlightColor) {
    this._draftColor = value;
  }

  setDraftColor(value: PdfNoteHighlightColor) {
    this._setDraftColor(value);
  }

  async loadCounts() {
    this.countsMeta.setLoadedStartMeta();
    const counts = await this._api.getCountsByDocument(this._organizationId);

    runInAction(() => {
      this.counts.clear();
      Object.entries(counts).forEach(([documentId, count]) => {
        if (count > 0) {
          this.counts.set(documentId, count);
        }
      });
      this.countsMeta.setLoadedSuccessMeta();
    });
  }

  async loadCurrentDocument() {
    const documentId = this.documentId;

    if (!documentId) {
      this._setCollection([]);
      this.meta.reset();
      this._setAiSummaryNote(null);
      this._setAiSummaryStreamPreview('');
      this.aiSummaryMeta.reset();

      return;
    }

    if (!UUID_REGEX.test(documentId)) {
      this._setCollection([]);
      this._setAiSummaryNote(null);
      this._setAiSummaryStreamPreview('');
      this.meta.setLoadedErrorMeta();
      notificationsStore.showError(INVALID_DOCUMENT_ID_MESSAGE);

      return;
    }

    this.meta.setLoadedStartMeta();

    const [notes, latestResponse] = await Promise.all([
      this._api.listByDocument(this._organizationId, documentId),
      api<MeetingNoteSummaryResponseApi>({
        endpoint: ENDPOINTS.documents.meetingNotesLatest(this._organizationId, documentId),
        method: 'GET',
      }),
    ]);

    const latestStatusError = latestResponse.isError ? (latestResponse.error.response?.status ?? null) : null;
    const parsed = latestResponse.isError ? null : schemeParse(meetingNoteSummarySchema, latestResponse.data);
    const latestSummary = parsed?.success ? parsed.data : null;

    if (latestStatusError === 400) {
      notificationsStore.showError(INVALID_DOCUMENT_ID_MESSAGE);
    } else if (latestStatusError && latestStatusError !== 404) {
      notificationsStore.showError('Не удалось загрузить последнее саммари по заметкам');
    }

    runInAction(() => {
      if (this.documentId !== documentId) {
        return;
      }

      const aiFromApi = notes.find((n) => n.isAiDocumentSummary);
      const rest = notes.filter((n) => !n.isAiDocumentSummary);

      this._setCollection(rest);

      if (latestSummary) {
        this._setAiSummaryNote({
          id: latestSummary.id,
          organizationId: this._organizationId,
          documentId,
          caption: 'Саммари по заметкам',
          text: latestSummary.content,
          pageNumber: 1,
          quote: '',
          rects: [],
          createdAt: latestSummary.createdAt,
          updatedAt: latestSummary.updatedAt,
          isAiDocumentSummary: true,
        });
      } else if (aiFromApi) {
        this._setAiSummaryNote(aiFromApi);
      } else {
        this._setAiSummaryNote(null);
      }

      if (latestStatusError && latestStatusError !== 404) {
        this.meta.setLoadedErrorMeta();
      } else {
        this.meta.setLoadedSuccessMeta();
      }
    });
  }

  private _cancelMeetingNotesStream() {
    if (this._meetingNotesAbortController) {
      this._meetingNotesAbortController.abort();
      this._meetingNotesAbortController = null;
    }
  }

  openDrawer() {
    this._setDrawerOpened(true);
  }

  closeDrawer() {
    this._setDrawerOpened(false);
    this._setDetailNoteId(null);
    this._setEditingNoteId(null);
    this._setCreateMode(false);
  }

  openDetail(noteId: string) {
    this._setDrawerOpened(true);
    this._setCreateMode(false);
    this._setEditingNoteId(null);
    this._setDetailNoteId(noteId);
    this._setActiveNoteId(noteId);
  }

  backToList() {
    this._setDetailNoteId(null);
    this._setEditingNoteId(null);
    this._setCreateMode(false);
  }

  setActiveNote(noteId: string | null) {
    this._setActiveNoteId(noteId);
  }

  openFromHighlight(noteId: string) {
    this._setDrawerOpened(true);
    this._setCreateMode(false);
    this._setEditingNoteId(null);
    this._setDetailNoteId(null);
    this._setActiveNoteId(noteId);
  }

  setPendingSelection(selection: PdfNoteSelection | null) {
    this._setPendingSelection(selection);
  }

  clearPendingSelection() {
    this._setPendingSelection(null);
  }

  openCreateFromSelection(selection?: PdfNoteSelection | null, options?: { openDrawer?: boolean }) {
    const nextSelection = selection ?? this._pendingSelection;
    const pageNumber = nextSelection?.pageNumber ?? this._getCurrentPage();
    const textFromSelection = nextSelection?.quote ?? '';
    const documentName = this._getDocumentName()?.trim() || 'Документ';
    const defaultCaption = `${documentName} - стр. ${pageNumber}`;
    const shouldOpenDrawer = options?.openDrawer ?? true;
    const color = nextSelection?.color ?? 'blue';

    this._setPendingSelection(nextSelection ?? null);
    this.draftCaptionField.setValue(defaultCaption);
    this.draftTextField.setValue(textFromSelection);
    this._setDraftColor(color);
    this._setCreateMode(true);
    this._setEditingNoteId(null);
    this._setDetailNoteId(null);
    this._setDrawerOpened(shouldOpenDrawer);
  }

  openEdit(noteId: string) {
    if (this._aiSummaryNote?.id === noteId) {
      return;
    }

    const note = this._collection.find((item) => item.id === noteId);

    if (!note) {
      return;
    }

    this._setCreateMode(true);
    this._setEditingNoteId(note.id);
    this._setDetailNoteId(null);
    this._setActiveNoteId(note.id);
    this._setPendingSelection(null);
    this.draftCaptionField.setValue(note.caption);
    this.draftTextField.setValue(note.text);
    this._setDraftColor(note.color ?? 'blue');
    this._setDrawerOpened(true);
  }

  openCreateFromCurrentPage() {
    this.openCreateFromSelection(null);
  }

  cancelCreate() {
    this._setCreateMode(false);
    this._setEditingNoteId(null);
    this.draftCaptionField.setValue('');
    this.draftTextField.setValue('');
    this._setPendingSelection(null);
  }

  async createNote() {
    const documentId = this.documentId;

    if (!documentId || this.createMeta.isLoading) {
      return;
    }

    const caption = (this.draftCaptionField.value ?? '').trim();
    const text = (this.draftTextField.value ?? '').trim();
    const editingNoteId = this._editingNoteId;

    if (editingNoteId) {
      if (!caption) {
        return;
      }

      this.createMeta.setLoadedStartMeta();

      const note = await this._api.update(this._organizationId, editingNoteId, {
        caption,
        text,
        color: this._draftColor,
      });

      if (!note) {
        runInAction(() => {
          this.createMeta.setLoadedErrorMeta();
        });

        return;
      }

      runInAction(() => {
        this._setCreateMode(false);
        this._setEditingNoteId(null);
        this.draftCaptionField.setValue('');
        this.draftTextField.setValue('');
        this._setPendingSelection(null);
        this._setActiveNoteId(note.id);
        this._setDetailNoteId(null);
        this.createMeta.setLoadedSuccessMeta();
      });

      return;
    }

    const selection = this._pendingSelection;
    const quote = selection?.quote?.trim() ?? '';
    const isPointSelection = Boolean(selection?.point);
    const isAreaSelection = Boolean(selection?.path && selection.path.length > 1);
    const isPointOrArea = isPointSelection || isAreaSelection;

    if (!caption || (!isPointOrArea && !text && !quote)) {
      return;
    }

    this.createMeta.setLoadedStartMeta();

    const note = await this._api.create(this._organizationId, {
      documentId,
      documentName: this._getDocumentName()?.trim() || undefined,
      caption,
      text: text || quote,
      pageNumber: selection?.pageNumber ?? this._getCurrentPage(),
      quote,
      rects: selection?.rects?.length ? mergeRectsToSingleBlock(selection.rects) : [],
      anchorType: isAreaSelection ? 'area' : isPointSelection ? 'point' : (selection?.anchorType ?? 'text'),
      point: isPointSelection ? selection?.point : undefined,
      path: isAreaSelection ? selection?.path : undefined,
      color: this._draftColor,
    });

    if (!note) {
      runInAction(() => {
        this.createMeta.setLoadedErrorMeta();
      });

      return;
    }

    await Promise.all([this.loadCurrentDocument(), this.loadCounts()]);

    runInAction(() => {
      this._setCreateMode(false);
      this._setEditingNoteId(null);
      this.draftCaptionField.setValue('');
      this.draftTextField.setValue('');
      this._setPendingSelection(null);
      this._setActiveNoteId(note.id);
      this._setDetailNoteId(null);
      this.createMeta.setLoadedSuccessMeta();
    });

    notificationsStore.showSuccess('Заметка создана');
  }

  async deleteNote(noteId: string) {
    if (!noteId) {
      return;
    }

    if (this._aiSummaryNote?.id === noteId) {
      return;
    }

    await this._api.delete(this._organizationId, noteId);
    await Promise.all([this.loadCurrentDocument(), this.loadCounts()]);

    runInAction(() => {
      if (this._detailNoteId === noteId) {
        this._setDetailNoteId(null);
      }

      if (this._activeNoteId === noteId) {
        this._setActiveNoteId(null);
      }
    });
  }

  hasNotes(documentId: string): boolean {
    return (this.counts.get(documentId) ?? 0) > 0;
  }

  async generateAiDocumentSummary() {
    const documentId = this.documentId;

    if (!documentId || this._collection.length === 0 || this.aiSummaryMeta.isLoading) {
      return;
    }

    if (!UUID_REGEX.test(documentId)) {
      notificationsStore.showError(INVALID_DOCUMENT_ID_MESSAGE);
      this.aiSummaryMeta.setLoadedErrorMeta();

      return;
    }

    this.aiSummaryMeta.setLoadedStartMeta();
    this._setAiSummaryStreamPreview('');
    this._cancelMeetingNotesStream();
    this._meetingNotesAbortController = new AbortController();
    const streamAbortController = this._meetingNotesAbortController;

    try {
      const streamState: {
        completeSummary: {
          id: string;
          content: string;
          createdAt: string;
          updatedAt: string;
        } | null;
        streamError: string | null;
      } = {
        completeSummary: null,
        streamError: null,
      };

      await this._sse.stream({
        url: ENDPOINTS.documents.meetingNotesStream(this._organizationId, documentId),
        body: {},
        signal: streamAbortController.signal,
        handlers: {
          onToken: (content) => {
            if (this.documentId !== documentId) {
              return;
            }

            runInAction(() => {
              this._setAiSummaryStreamPreview(content);
            });
          },
          onAnswerRestart: (message) => {
            if (this.documentId !== documentId) {
              return;
            }

            runInAction(() => {
              this._setAiSummaryStreamPreview('');
            });
            notificationsStore.showWarning(message);
          },
          onStatus: (message) => {
            if (this.documentId !== documentId) {
              return;
            }

            if (!this._aiSummaryStreamPreview) {
              runInAction(() => {
                this._setAiSummaryStreamPreview(message);
              });
            }
          },
          onComplete: (summary) => {
            streamState.completeSummary = {
              id: summary.id,
              content: summary.content,
              createdAt: summary.createdAt,
              updatedAt: summary.updatedAt,
            };
          },
          onError: (message) => {
            streamState.streamError = message;
          },
        },
      });

      if (streamState.streamError) {
        runInAction(() => {
          this._setAiSummaryStreamPreview('');
          this.aiSummaryMeta.setLoadedErrorMeta();
        });
        notificationsStore.showError(streamState.streamError);

        return;
      }

      if (!streamState.completeSummary) {
        runInAction(() => {
          this._setAiSummaryStreamPreview('');
          this.aiSummaryMeta.setLoadedErrorMeta();
        });
        notificationsStore.showError('Не удалось сформировать саммари по заметкам');

        return;
      }

      const summary = streamState.completeSummary;

      runInAction(() => {
        if (this.documentId !== documentId) {
          return;
        }

        this._setAiSummaryNote({
          id: summary.id,
          organizationId: this._organizationId,
          documentId,
          caption: 'Саммари по заметкам',
          text: summary.content,
          pageNumber: 1,
          quote: '',
          rects: [],
          createdAt: summary.createdAt,
          updatedAt: summary.updatedAt,
          isAiDocumentSummary: true,
        });
        this._setAiSummaryStreamPreview('');
        this.aiSummaryMeta.setLoadedSuccessMeta();
      });

      notificationsStore.showSuccess('Саммари по заметкам готово');
    } catch (error: unknown) {
      if ((error as Error)?.name === 'AbortError') {
        return;
      }

      const maybeMessage = error instanceof Error ? error.message : null;
      const isValidationError = maybeMessage?.includes('400') || maybeMessage === INVALID_DOCUMENT_ID_MESSAGE;

      runInAction(() => {
        this._setAiSummaryStreamPreview('');
        this.aiSummaryMeta.setLoadedErrorMeta();
      });

      notificationsStore.showError(
        isValidationError ? INVALID_DOCUMENT_ID_MESSAGE : 'Не удалось сформировать саммари по заметкам',
      );
    } finally {
      if (this._meetingNotesAbortController === streamAbortController) {
        this._meetingNotesAbortController = null;
      }
    }
  }

  destroy(): void {
    this._cancelMeetingNotesStream();
    this._documentReactionDisposer?.();
    this._documentReactionDisposer = null;
    this._unsubscribeRepository?.();
    this._unsubscribeRepository = null;
    this.meta.destroy();
    this.aiSummaryMeta.destroy();
    this.createMeta.destroy();
    this.countsMeta.destroy();
    this.counts.clear();
    this.draftCaptionField.destroy();
    this.draftTextField.destroy();
  }
}
